import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { CITY_VIEW, LoginCityArtwork } from "./LoginCityArtwork";

/**
 * The login's marketing panel, as Figma's "Component 2" (89:1573 in the
 * animation file): the city, the live tracking over it (vehicles circling
 * their routes with pins, the depot's beam and chip, alerts and vehicle
 * cards taking turns, the live tag, the stats) and the copy.
 *
 * Sized in Figma's own units: --u is one of them, as large as the panel
 * allows for Figma's 751x976 (styles.css, "Login art"). The copy, live tag
 * and stats keep Figma's 44-unit margin from the panel's own edges, so a
 * wider panel than Figma's doesn't add side padding; the city's scene stays
 * Figma-sized in the middle. On a squat panel (as wide as it is tall, where
 * the height is what limits --u) the copy's spacing tightens and the city
 * grows into the room that frees. The scene isn't mirrored; the copy, chips
 * and cards follow the language (Arabic on the right as in Figma).
 *
 * Only transform and opacity animate, on HTML boxes, so the compositor runs
 * them. Each vehicle's loop is its own generated keyframes with the moves
 * written in (keyframes that read custom properties can't be composited).
 */

const SCENE = { width: 751, height: 976 } as const;

/** n Figma units, at the panel's size. */
function u(n: number): string {
  return `calc(var(--u) * ${String(n)})`;
}

function at(x: number, y: number, width?: number, height?: number): CSSProperties {
  return {
    left: u(x),
    top: u(y),
    ...(width === undefined ? {} : { width: u(width) }),
    ...(height === undefined ? {} : { height: u(height) }),
  };
}

type Tone = "lavender" | "purple" | "green" | "amber" | "pink";

/**
 * Alerts and vehicle cards take turns, a third of the round each, so none
 * covers another: the speeding alert (right) with the left vehicle's card,
 * the idle alert (left) with the right vehicle's card, then the centre
 * vehicle's card alone. Measured: left on together, they overlapped 20-58%
 * of the time.
 */
const TURN_ROUND_S = 15;
const FIRST_TURN_S = 1.4;

function turnDelay(turn: 0 | 1 | 2): string {
  return `${String(FIRST_TURN_S + (turn * TURN_ROUND_S) / 3)}s`;
}

/** A route: the diamond a vehicle circles, as Figma's box around it. */
interface Route {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function corners({ x, y, width, height }: Route) {
  return {
    top: { x: x + width / 2, y },
    right: { x: x + width, y: y + height / 2 },
    bottom: { x: x + width / 2, y: y + height },
    left: { x, y: y + height / 2 },
  };
}

type Corner = keyof ReturnType<typeof corners>;

const CLOCKWISE: readonly Corner[] = ["top", "right", "bottom", "left"];

interface VehicleCard {
  readonly plate: string;
  readonly speed: number;
  readonly arrival: string;
  /** How far along its trip, 0-1. */
  readonly progress: number;
  readonly turn: 0 | 1 | 2;
}

/** Figma's six vehicles: each circles its route from where Figma draws it,
 *  four with a pin over them, three with a card in their turn. */
const VEHICLES: ReadonlyArray<{
  route: Route;
  tone: Tone;
  pin: boolean;
  from: Corner;
  clockwise: boolean;
  card?: VehicleCard;
}> = [
  {
    route: { x: 243.5, y: 557.5, width: 264, height: 132 },
    tone: "lavender",
    pin: true,
    from: "top",
    clockwise: true,
    // Figma's tracked card.
    card: { plate: "VH-1042", speed: 58, arrival: "17:40", progress: 0.29, turn: 2 },
  },
  {
    route: { x: 111.5, y: 601.5, width: 176, height: 88 },
    tone: "purple",
    pin: true,
    from: "left",
    clockwise: false,
    card: { plate: "VH-2213", speed: 46, arrival: "18:05", progress: 0.55, turn: 0 },
  },
  {
    route: { x: 463.5, y: 601.5, width: 176, height: 88 },
    tone: "green",
    pin: true,
    from: "bottom",
    clockwise: true,
    card: { plate: "VH-0871", speed: 64, arrival: "17:25", progress: 0.72, turn: 1 },
  },
  { route: { x: 287.5, y: 469.5, width: 176, height: 88 }, tone: "purple", pin: true, from: "left", clockwise: true },
  { route: { x: 287.5, y: 689.5, width: 176, height: 88 }, tone: "lavender", pin: false, from: "top", clockwise: false },
  { route: { x: 375.5, y: 535.5, width: 264, height: 132 }, tone: "green", pin: false, from: "right", clockwise: false },
];

/** Figma units a second: calm, a big loop in about 25 s. */
const VEHICLE_SPEED = 24;
/** The vehicle's dot sits a little above the route's line. */
const CORE_LIFT = 2.5;

function loopOf({ route, from, clockwise }: (typeof VEHICLES)[number]) {
  const points = corners(route);
  const order = clockwise ? CLOCKWISE : [...CLOCKWISE].reverse();
  const start = order.indexOf(from);
  const path = [0, 1, 2, 3].map((step) => points[order[(start + step) % 4] ?? from]);
  const side = Math.hypot(route.width / 2, route.height / 2);
  return { path, seconds: (4 * side) / VEHICLE_SPEED };
}

/**
 * Each vehicle's loop as keyframes, with the moves written in. A vehicle's
 * box is 1% of the scene each way, so translate percentages of the box
 * scale with the scene.
 */
const VEHICLE_KEYFRAMES = VEHICLES.map((vehicle, index) => {
  const { path } = loopOf(vehicle);
  const [start] = path;
  if (start === undefined) return "";
  const steps = [...path, start].map((point, step) => {
    const x = (((point.x - start.x) / SCENE.width) * 10_000).toFixed(1);
    const y = (((point.y - start.y) / SCENE.height) * 10_000).toFixed(1);
    return `${String(step * 25)}%{transform:translate(${x}%,${y}%)}`;
  });
  return `@keyframes login-art-drive-${String(index)}{${steps.join("")}}`;
}).join("");

function MapPin({ tone }: { readonly tone: Tone }) {
  return (
    <svg viewBox="0 0 40 48" fill="none" className="login-art-pin" data-tone={tone}>
      <path
        d="M20 38C20 38 10 27 10 20C10 17.35 11.05 14.8 12.93 12.93C14.8 11.05 17.35 10 20 10C22.65 10 25.2 11.05 27.07 12.93C28.95 14.8 30 17.35 30 20C30 27 20 38 20 38Z"
        fill="currentColor"
      />
      <circle cx="20" cy="20" r="4" fill="white" />
    </svg>
  );
}

/** A dark see-through chip with a coloured dot, as Figma's. */
function Chip({ tone, children }: { readonly tone: Tone; readonly children: ReactNode }) {
  return (
    <span className="login-art-chip">
      <span className="login-art-dot" data-tone={tone} />
      {children}
    </span>
  );
}

/** Figma's tracked card, riding above its vehicle in its turn. */
function TrackedCard({ card, dir }: { readonly card: VehicleCard; readonly dir: "ltr" | "rtl" }) {
  const { t } = useTranslation("authentication", { keyPrefix: "loginArt" });
  return (
    <div
      dir={dir}
      className="login-art-surface login-art-vehicle-card"
      style={{ animationDelay: turnDelay(card.turn) }}
    >
      <div className="login-art-tracked-row" style={{ marginTop: u(9) }}>
        <span dir="ltr" className="login-art-plate">
          {card.plate}
        </span>
        <span className="login-art-status">
          {t("tracked.status")}
          <span className="login-art-dot" data-tone="green" />
        </span>
      </div>
      <div className="login-art-tracked-row" style={{ marginTop: u(1) }}>
        <span className="login-art-speed">
          <span className="login-art-speed-value">{card.speed}</span>
          {t("tracked.unit")}
        </span>
        <span className="login-art-arrival">{t("tracked.arrival", { time: card.arrival })}</span>
      </div>
      <span className="login-art-progress">
        <span
          className={dir === "rtl" ? "login-art-progress-fill login-art-progress-rtl" : "login-art-progress-fill"}
          style={{ width: `${String(Math.round(card.progress * 100))}%` }}
        />
      </span>
    </div>
  );
}

const ALERTS: ReadonlyArray<{ key: "speeding" | "idle"; x: number; tone: Tone; turn: 0 | 1 }> = [
  { key: "speeding", x: 595.5, tone: "pink", turn: 0 },
  { key: "idle", x: 155.5, tone: "amber", turn: 1 },
];

const STATS = [
  { key: "onRoad", value: "42", tone: "lavender" },
  { key: "maintenance", value: "3", tone: "amber" },
  { key: "onTime", value: "96.4%", tone: "green" },
] as const satisfies ReadonlyArray<{ key: string; value: string; tone: Tone }>;

export function LoginArtwork() {
  const { t, i18n } = useTranslation("authentication", { keyPrefix: "loginArt" });
  const dir = i18n.dir();

  return (
    <div className="login-art" data-node-id="89:1573">
      {/* The scene is decoration, Figma-sized in the middle; the copy below
          is read out. */}
      <div className="login-art-scene" aria-hidden="true">
        <span className="login-art-glow" style={at(65.5, 520, 620, 420)} />

        <div className="login-art-city" style={at(CITY_VIEW.x, CITY_VIEW.y, CITY_VIEW.width)}>
          <LoginCityArtwork />
        </div>

        {/* Tracking (89:1813): drawn over the city, as Figma does. The beam
            fades in, then pulses inside: two opacity animations on one box
            can't both run on the compositor. */}
        <span className="login-art-beam-wrap" style={at(355.5, 416, 40, 170)}>
          <span className="login-art-beam" />
        </span>
        <svg viewBox={`0 0 ${String(SCENE.width)} ${String(SCENE.height)}`} fill="none" className="login-art-routes">
          {VEHICLES.map(({ route, tone }, index) => {
            const { top, right, bottom, left } = corners(route);
            return (
              <polygon
                key={index}
                data-tone={tone}
                points={[top, right, bottom, left].map(({ x, y }) => `${String(x)},${String(y)}`).join(" ")}
              />
            );
          })}
        </svg>

        <style>{VEHICLE_KEYFRAMES}</style>
        {VEHICLES.map((vehicle, index) => {
          const { path, seconds } = loopOf(vehicle);
          const [start] = path;
          if (start === undefined) return null;
          return (
            <span
              key={index}
              className="login-art-vehicle"
              data-tone={vehicle.tone}
              data-card={vehicle.card === undefined ? undefined : vehicle.card.turn === 2 ? "still" : "turn"}
              style={{
                left: `${((start.x / SCENE.width) * 100).toFixed(3)}%`,
                top: `${(((start.y - CORE_LIFT) / SCENE.height) * 100).toFixed(3)}%`,
                animationName: `login-art-drive-${String(index)}`,
                animationDuration: `${seconds.toFixed(1)}s`,
              }}
            >
              <span className="login-art-halo" />
              <span className="login-art-core" />
              {vehicle.pin ? (
                <span className="login-art-vehicle-pin">
                  <MapPin tone={vehicle.tone} />
                </span>
              ) : null}
              {vehicle.card === undefined ? null : <TrackedCard card={vehicle.card} dir={dir} />}
            </span>
          );
        })}

        <span dir={dir} className="login-art-anchor" style={at(375.5, 382)}>
          <Chip tone="green">{t("depot")}</Chip>
        </span>

        {ALERTS.map(({ key, x, tone, turn }) => (
          <span
            key={key}
            dir={dir}
            className="login-art-anchor login-art-alert"
            data-alert={key}
            style={{ ...at(x, 594), animationDelay: turnDelay(turn) }}
          >
            <Chip tone={tone}>{t(`alerts.${key}`)}</Chip>
            <span className="login-art-alert-pin">
              <MapPin tone={tone} />
            </span>
          </span>
        ))}
      </div>

      {/* At Figma's margin from the panel's edges, like the copy. */}
      <div aria-hidden="true" dir={dir} className="login-art-live-tag">
        <span className="login-art-dot login-art-dot-live" data-tone="green" />
        {t("liveTag")}
      </div>

      <ul aria-hidden="true" dir={dir} className="login-art-stats">
        {STATS.map(({ key, value, tone }) => (
          <li key={key} className="login-art-surface login-art-stat">
            <span className="login-art-dot" data-tone={tone} />
            <span className="login-art-stat-value">{value}</span>
            <span className="login-art-stat-label">{t(`stats.${key}`)}</span>
          </li>
        ))}
      </ul>

      {/* Copy (89:1885). */}
      <div dir={dir} className="login-art-copy">
        <div aria-hidden="true" className="login-art-brand">
          <img src="/brand/logo.png" alt="" className="login-art-logo" />
          <span dir="ltr" className="login-art-wordmark">
            NASAM
          </span>
        </div>
        <p className="login-art-eyebrow">
          <span className="login-art-dot login-art-dot-live" data-tone="green" />
          {t("eyebrow")}
        </p>
        <h2 className="login-art-headline">
          {/* The space keeps the two lines apart when read out. */}
          {t("headline")}{" "}
          <br />
          <span className="login-art-headline-accent">{t("headlineAccent")}</span>
        </h2>
        <p className="login-art-lead">
          {t("lead")}
        </p>
      </div>
    </div>
  );
}
